export const findDefaultDecision = (analysis) => {
  if (!analysis?.success) {
    return "skip";
  }
  if (analysis.officeMatchBlocked) {
    return "skip";
  }
  if (analysis.identityMatchCandidate) {
    return "match";
  }
  return "new";
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// CSV/date-only strings ("YYYY-MM-DD") have no time component, so handing
// them to `new Date()` parses them as UTC midnight — one day early for any
// user west of UTC. Build the date from local components instead.
//
// The Date constructor normalizes out-of-range components instead of
// rejecting them (month 0 rolls back into the prior December, month 99
// rolls forward years) -- a malformed-but-regex-shaped value like
// "2024-00-10" or "2024-99-01" would otherwise silently become some
// unrelated real date instead of getting caught as invalid. Confirm the
// constructed date's components still match what was parsed before trusting it.
const parseDateOnlyLocal = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const isValidCalendarDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;
  return isValidCalendarDate ? date : new Date(NaN);
};

const parseStartDateValue = (value) => {
  if (value.seconds) {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
    return parseDateOnlyLocal(value);
  }
  return new Date(value);
};

export const toStartDateObj = (value) => {
  if (!value) {
    return null;
  }
  const date = parseStartDateValue(value);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * The date commuting emissions will actually start counting from for a row.
 * Mirrors the backend resolution order in employeeBulkImport.commitSingleRow:
 * the CSV start date column, then the company start date fallback, then today.
 */
export const findEffectiveCommuteStartDate = ({
  row = {},
  companyStartDate,
  useCompanyStartDateFallback = true
}) => {
  const csvDate = toStartDateObj(row.startDate);
  if (csvDate) {
    return { date: csvDate, source: "csv" };
  }

  const companyDate = useCompanyStartDateFallback
    ? toStartDateObj(companyStartDate)
    : null;
  if (companyDate) {
    return { date: companyDate, source: "company" };
  }

  return { date: new Date(), source: "today" };
};
